"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface ZoneCard {
  id: string;
  name: string;
  subtitle: string;
  imageUrl: string;
  imageCredit: string;
  imageAlt: string;
  /** 2-3 short metric badges shown above the description. */
  badges: readonly { label: string; value: string }[];
  description: string;
  /** One-line opinionated verdict at the bottom. */
  verdict: string;
}

interface ZoneCarouselProps {
  zones: readonly ZoneCard[];
}

/**
 * Horizontal scroll-snap carousel of zone cards. Apple-style: track
 * scrolls smoothly, prev/next buttons on desktop, dot indicators
 * always. Mobile users swipe; desktop users click arrows or scroll
 * with trackpad.
 *
 * Each card has a photo, zone name + subtitle, 2-3 metric badges
 * (cap rate, ticket, etc.), description, and a 1-line verdict.
 */
export function ZoneCarousel({ zones }: ZoneCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Update active dot based on scroll position.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onScroll = () => {
      const cardWidth = track.scrollWidth / zones.length;
      const idx = Math.round(track.scrollLeft / cardWidth);
      setActiveIdx(Math.min(Math.max(idx, 0), zones.length - 1));
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [zones.length]);

  const scrollTo = useCallback(
    (idx: number) => {
      const track = trackRef.current;
      if (!track) return;
      const cardWidth = track.scrollWidth / zones.length;
      track.scrollTo({ left: cardWidth * idx, behavior: "smooth" });
    },
    [zones.length]
  );

  const prev = () => scrollTo(Math.max(activeIdx - 1, 0));
  const next = () => scrollTo(Math.min(activeIdx + 1, zones.length - 1));

  return (
    <section aria-label="Zonas para invertir en RD" className="my-12 sm:my-16">
      <div className="relative">
        {/* Track — breaks out of body wrappers via wider negative margins
            so cards have room to peek and the snap motion feels natural. */}
        <div
          ref={trackRef}
          className="-mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-8 sm:gap-6 sm:px-8 lg:-mx-10 lg:px-10"
        >
          {zones.map((zone, idx) => {
            const isActive = idx === activeIdx;
            return (
              <article
                key={zone.id}
                aria-current={isActive}
                className={`flex w-[88%] shrink-0 snap-center flex-col overflow-hidden rounded-3xl bg-white shadow-md ring-1 transition-all duration-300 ease-out sm:w-[72%] lg:w-[55%] ${
                  isActive
                    ? "scale-100 opacity-100 ring-coral/30"
                    : "scale-[0.96] opacity-70 ring-ink-200"
                }`}
                style={{ transformOrigin: "center" }}
              >
                <div className="relative aspect-[4/3] w-full bg-cream-100 sm:aspect-[16/10]">
                  <Image
                    src={zone.imageUrl}
                    alt={zone.imageAlt}
                    fill
                    sizes="(max-width: 640px) 88vw, (max-width: 1024px) 72vw, 55vw"
                    className="object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 via-ink/50 to-transparent p-5 sm:p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-coral sm:text-xs">
                      {zone.subtitle}
                    </p>
                    <h3 className="mt-1 text-xl font-extrabold leading-tight text-cream-50 sm:text-2xl lg:text-3xl">
                      {zone.name}
                    </h3>
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-4 p-5 sm:p-6 lg:p-7">
                  <div className="flex flex-wrap gap-2">
                    {zone.badges.map((badge, i) => (
                      <div
                        key={i}
                        className="inline-flex items-baseline gap-1.5 rounded-full bg-cream-100 px-3 py-1 text-xs"
                      >
                        <span className="font-medium uppercase tracking-wider text-ink-500">
                          {badge.label}
                        </span>
                        <span className="font-bold text-ink">
                          {badge.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed text-ink-700 sm:text-base">
                    {zone.description}
                  </p>
                  <p className="mt-auto border-t border-ink-200 pt-4 text-sm font-medium italic text-ink">
                    {zone.verdict}
                  </p>
                  <p className="text-xs text-ink-500">
                    Foto: {zone.imageCredit}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* Prev/next buttons — hidden on mobile (swipe instead). On
            desktop they sit OVER the carousel for clear affordance, with
            translucent backgrounds so they don't fight the cards. */}
        <button
          type="button"
          onClick={prev}
          aria-label="Zona anterior"
          disabled={activeIdx === 0}
          className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-ink/90 p-3 text-cream-50 shadow-xl backdrop-blur-sm transition hover:bg-ink disabled:cursor-not-allowed disabled:opacity-30 sm:block"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Zona siguiente"
          disabled={activeIdx === zones.length - 1}
          className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-ink/90 p-3 text-cream-50 shadow-xl backdrop-blur-sm transition hover:bg-ink disabled:cursor-not-allowed disabled:opacity-30 sm:block"
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {/* Dot indicators */}
      <div className="mt-4 flex justify-center gap-2">
        {zones.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => scrollTo(idx)}
            aria-label={`Ir a zona ${idx + 1}`}
            aria-current={idx === activeIdx}
            className={`h-2 rounded-full transition-all ${
              idx === activeIdx
                ? "w-8 bg-coral"
                : "w-2 bg-ink-200 hover:bg-ink-500"
            }`}
          />
        ))}
      </div>

      {/* Counter */}
      <p className="mt-3 text-center text-xs text-ink-500">
        {activeIdx + 1} / {zones.length} zonas
      </p>
    </section>
  );
}
